import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import { useCallback, useContext } from 'react';
import { LogAction } from '../constants';
import { LoggerDataContext, LoggerDataContextType } from '../context';
import { Logger } from '../Logger';
import { DomainSpecificInfoType, FeatureInfo, LoggerType, TrackingData } from '../models';

type HookResponse = {
  flush: () => void;
  trackEvent: (trackingData: TrackingData, contextData?: LoggerDataContextType, loggerInstances?: LoggerType[]) => void;
  trackEventOnClick: (
    onClick?: (e: React.MouseEvent | MouseEvent) => void,
    trackingData?: Partial<TrackingData>,
    contextData?: Partial<LoggerDataContextType>
  ) => (e: React.MouseEvent | MouseEvent) => void;
};
// logger hook
export const useLogger = (componentName?: string): HookResponse => {
  const loggerContext = useContext(LoggerDataContext);

  /**
   * Track event function which combines context data with provided tracking data.
   *
   * Reads current context values via getValues() to ensure latest data is captured
   *
   * @param trackingData - tracking data to be sent
   * @param contextData - context data override (takes precedence over context values)
   * @param loggerInstances - logger instances override to which event should be sent
   */
  const trackEvent = useCallback(
    (trackingData: TrackingData, contextData?: LoggerDataContextType, loggerInstances?: LoggerType[]) => {
      try {
        const loggerContextData = loggerContext.getValues();
        const featureInfo: FeatureInfo = {
          featureName: trackingData.featureInfo?.featureName ?? contextData?.feature ?? loggerContextData.feature,
          featureLocal:
            trackingData.featureInfo?.featureLocal ?? contextData?.featureLocal ?? loggerContextData.featureLocal,
          sectionName: trackingData.featureInfo?.sectionName ?? contextData?.section ?? loggerContextData.section,
          sectionLocal:
            trackingData.featureInfo?.sectionLocal ?? contextData?.sectionLocal ?? loggerContextData.sectionLocal,
        };
        // deep merge domainSpecificInfo from context and trackingData
        const domainSpecificInfo: DomainSpecificInfoType | undefined = merge(
          {},
          contextData?.domainSpecificInfo ?? loggerContextData.domainSpecificInfo,
          trackingData.domainSpecificInfo
        );
        trackingData = {
          ...trackingData,
          activeTab: contextData?.activeTab ?? loggerContextData.activeTab,
          activeView: contextData?.activeView ?? loggerContextData.activeView,
          componentName,
          featureInfo,
          ...(!isEmpty(domainSpecificInfo) ? { domainSpecificInfo } : {}),
        };
        Logger.trackEvent(trackingData, loggerInstances);
      } catch (error) {
        console.error('Logger: error in trackEvent hook', error);
      }
    },
    [componentName, loggerContext]
  );

  /**
   * Helper function to wrap around onClick event
   */
  const trackEventOnClick =
    (
      onClick?: (e: React.MouseEvent | MouseEvent) => void,
      trackingData?: Partial<TrackingData>,
      contextData?: Partial<LoggerDataContextType>
    ) =>
    (e: React.MouseEvent | MouseEvent) => {
      try {
        onClick?.(e);
        e.stopPropagation();
        trackEvent({ action: LogAction.ELEMENT_CLICKED, srcElement: e.target, ...trackingData }, contextData);
      } catch (error) {
        console.error('Logger: error in trackEventOnClick hook', error);
      }
    };

  const flush = useCallback(() => {
    Logger.flush();
  }, []);

  return { flush, trackEvent, trackEventOnClick };
};
